import React from "react";
import { Badge } from "react-bootstrap";

// "above"/"below" describen la clase RESPECTO AL nivel del estudiante que
// consulta (ver backend: operations/api/serializers/planning.py
// ::get_level_comparison) — "above" = más difícil que su nivel, "below" =
// más fácil, "same" = corresponde a su nivel.
const COMPARISON = {
  same: { icon: "mdi-check-circle-outline", variant: "success", label: "Tu nivel" },
  above: { icon: "mdi-arrow-up-bold-circle-outline", variant: "warning", label: "Más difícil" },
  below: { icon: "mdi-arrow-down-bold-circle-outline", variant: "info", label: "Más fácil" },
};

/**
 * LevelBadge — el nivel de una clase, opcionalmente comparado contra el
 * propio nivel del estudiante (icono + color: verde = coincide, naranja =
 * por encima, azul = por debajo). Sin nivel en la clase, no renderiza nada
 * — no hay nada que mostrar, y no es un error.
 *
 * `comparison` se omite (o llega null) para el docente/propietario: ellos
 * definen el nivel, no tiene sentido "compararlo" contra el suyo propio.
 */
function LevelBadge({ level, comparison, className }) {
  if (!level?.name) return null;

  const cmp = comparison ? COMPARISON[comparison] : null;

  return (
    <Badge
      bg={cmp?.variant ?? "secondary"}
      className={`d-inline-flex align-items-center gap-1 ${className ?? ""}`}
      title={cmp ? `${level.name} · ${cmp.label}` : level.name}
    >
      {cmp && <i className={`mdi ${cmp.icon}`} aria-hidden="true" />}
      {level.name}
    </Badge>
  );
}

/**
 * LevelIcon — versión compacta de LevelBadge para espacios angostos como el
 * evento del calendario (react-big-calendar en vista mes deja una sola línea
 * por evento). Muestra la abreviatura del nivel (p. ej. "A2") en vez del
 * nombre completo; sin abreviatura cargada, cae al nombre. El nombre
 * completo y la comparación quedan en el `title` nativo (mismo patrón que el
 * ícono de "repite un tema" ya usado en ClassesCalendar.js).
 */
function LevelIcon({ level, comparison, className }) {
  if (!level?.name) return null;

  const cmp = comparison ? COMPARISON[comparison] : null;
  const label = cmp ? `${level.name} · ${cmp.label}` : level.name;
  const colorClass = cmp ? `text-${cmp.variant}` : "text-secondary";
  const text = level.abbreviation || level.name;

  return (
    <span
      className={`small fw-semibold text-nowrap ${colorClass} ${className ?? ""}`}
      title={label}
      aria-label={label}
    >
      {cmp && <i className={`mdi ${cmp.icon} me-1`} aria-hidden="true" />}
      {text}
    </span>
  );
}

export { LevelBadge, LevelIcon };
